/**
 * Utility hook for managing component mount state and preventing state updates on unmounted components
 */
import { useEffect, useRef } from 'react';

/**
 * Hook that tracks if a component is mounted and provides a safe state setter
 * @returns Object with isMounted ref and createSafeSetter function
 */
export const useMountedState = () => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Creates a safe version of a state setter that only updates if component is mounted
   * @param setter The original state setter function
   * @returns Safe state setter that checks mount state
   */
  const createSafeSetter = <T>(setter: (value: T | ((prev: T) => T)) => void) => {
    return (value: T | ((prev: T) => T)) => {
      if (isMountedRef.current) {
        setter(value);
      }
    };
  };

  return {
    isMountedRef,
    createSafeSetter,
  };
};
